import React, { Component } from "react";
import "./Simulationcalculatorcdmqb.css";

// Same phone/tablet breakpoint the CDMQB test page uses.
const MOBILE_BREAKPOINT = 768;

const getIsMobile = () =>
  typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;

// Precise hit-area coordinates (percent of the calculator skin's own
// viewBox: 0 0 185.25 319.65) extracted directly from the reference
// artwork's button path geometry, so every hit-area sits exactly under
// its printed key.
const KEY_RECTS = {
  negate: { left: 8.049, top: 38.195, width: 16.594, height: 6.454 },
  sqrt: { left: 27.887, top: 38.195, width: 16.594, height: 6.454 },
  inv: { left: 47.849, top: 38.195, width: 16.594, height: 6.454 },
  divide: { left: 75.493, top: 38.195, width: 16.594, height: 6.454 },

  mrc: { left: 8.049, top: 47.511, width: 16.594, height: 6.454 },
  mminus: { left: 27.887, top: 47.511, width: 16.594, height: 6.454 },
  mplus: { left: 47.849, top: 47.511, width: 16.594, height: 6.454 },
  multiply: { left: 75.493, top: 47.511, width: 16.594, height: 6.454 },

  7: { left: 8.049, top: 56.778, width: 16.594, height: 6.454 },
  8: { left: 27.887, top: 56.778, width: 16.594, height: 6.454 },
  9: { left: 47.849, top: 56.778, width: 16.594, height: 6.454 },
  subtract: { left: 75.493, top: 56.778, width: 16.594, height: 6.454 },

  4: { left: 8.049, top: 66.147, width: 16.594, height: 6.454 },
  5: { left: 27.887, top: 66.147, width: 16.594, height: 6.454 },
  6: { left: 47.849, top: 66.147, width: 16.594, height: 6.454 },
  add: { left: 75.493, top: 66.147, width: 16.594, height: 6.454 },

  1: { left: 8.049, top: 75.464, width: 16.594, height: 6.454 },
  2: { left: 27.887, top: 75.464, width: 16.594, height: 6.454 },
  3: { left: 47.849, top: 75.464, width: 16.594, height: 6.454 },
  equals: { left: 75.493, top: 75.508, width: 16.594, height: 15.727 },

  clear: { left: 8.049, top: 84.78, width: 16.594, height: 6.454 },
  0: { left: 27.887, top: 84.78, width: 16.594, height: 6.454 },
  decimal: { left: 47.849, top: 84.78, width: 16.594, height: 6.454 },
};

const DISPLAY_RECT = { left: 12.955, top: 6.413, width: 74.494, height: 9.698 };

// ===========================================================================
// CdmqbCalculator — the calculator window itself (skin art, display, keys).
// Mounted only while the calculator is open, so closing it resets the value
// and position, same as before.
// ===========================================================================

class CdmqbCalculator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Inlined skin SVG markup (see componentDidMount).
      skinMarkup: null,

      // Calculator state (same behavior as testCalculator).
      calculatorValue: 0,
      adding: false,
      subtracting: false,
      multiplying: false,
      dividing: false,
      memory: 0,
      memoryValue: 0,
      decimal: false,

      // Floating window position (desktop only).
      position: { x: -500, y: 80 },
    };

    this.dragging = false;
    this.dragOffsetX = 0;
    this.dragOffsetY = 0;
  }

  componentDidMount() {
    this._isMounted = true;

    // The skin used to be loaded as <img src="...">, but Chromium-based
    // "force dark" modes (on by default in some privacy browsers, e.g.
    // Cromite) run a heuristic image classifier on <img>-loaded SVGs that
    // can selectively wipe out plain monochrome shapes like the digit
    // glyphs, and that heuristic can't be opted out of from CSS. Fetching
    // the SVG and inlining its markup makes the digits real DOM/SVG
    // elements instead, which forced-dark handles predictably and which
    // `color-scheme` can reliably exempt (see .cdmqb-calc-skin-art in the
    // CSS).
    fetch("/cdmqbcalculator-skin.svg")
      .then((res) => res.text())
      .then((svgText) => {
        if (this._isMounted) this.setState({ skinMarkup: svgText });
      })
      .catch(() => {});
  }

  componentWillUnmount() {
    this._isMounted = false;
    // In case the window is closed mid-drag.
    this.removeDragListeners();
  }

  // --- Dragging (desktop) ---------------------------------------------------

  handleDragStart = (e) => {
    if (this.props.isMobile) return;

    this.dragging = true;
    this.dragOffsetX = e.clientX - this.state.position.x;
    this.dragOffsetY = e.clientY - this.state.position.y;

    window.addEventListener("mousemove", this.handleDragMove);
    window.addEventListener("mouseup", this.handleDragEnd);
  };

  handleDragMove = (moveEvent) => {
    if (!this.dragging) return;
    this.setState({
      position: {
        x: moveEvent.clientX - this.dragOffsetX,
        y: moveEvent.clientY - this.dragOffsetY,
      },
    });
  };

  handleDragEnd = () => {
    this.dragging = false;
    this.removeDragListeners();
  };

  removeDragListeners = () => {
    window.removeEventListener("mousemove", this.handleDragMove);
    window.removeEventListener("mouseup", this.handleDragEnd);
  };

  // --- Calculator logic (same behavior as testCalculator) -------------------

  handleCalcNumber = (number, calculatorValue) => {
    const valueStr = calculatorValue.toString();
    if (calculatorValue === 0 && !valueStr.includes(".")) {
      this.setState({ calculatorValue: number });
    } else if (valueStr.includes(".")) {
      // Keep as string to preserve trailing zeros after decimal (e.g. "0.0", "0.04")
      this.setState({ calculatorValue: valueStr + `${number}` });
    } else {
      this.setState({ calculatorValue: Number(valueStr + `${number}`) });
    }
  };

  handleCalcClick = (buttonSelected, buttonColor) => {
    let calculatorValueString = this.state.calculatorValue.toString();
    let calculatorValue = this.state.calculatorValue;

    if (calculatorValueString[calculatorValueString.length - 1] === ".") {
      if (buttonColor !== "gray") {
        calculatorValue = Number(calculatorValue);
      }
    }

    switch (buttonSelected) {
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9":
        this.handleCalcNumber(Number(buttonSelected), calculatorValue);
        break;
      case "C":
        this.setState({
          calculatorValue: 0,
          multiplying: false,
          adding: false,
          subtracting: false,
          dividing: false,
        });
        break;
      case "+/-":
        this.setState((prevState) => ({
          calculatorValue: prevState.calculatorValue * -1,
        }));
        break;
      case "sqrt(x)":
        this.setState((prevState) => ({
          calculatorValue: parseFloat(
            Math.sqrt(prevState.calculatorValue).toFixed(12),
          ),
        }));
        break;
      case "1/x":
        this.setState((prevState) => ({
          calculatorValue: (1 / prevState.calculatorValue).toFixed(12),
        }));
        break;
      case "÷":
        this.setState((prevState) => ({
          dividing: true,
          adding: false,
          subtracting: false,
          multiplying: false,
          memory: prevState.calculatorValue,
          calculatorValue: 0,
        }));
        break;
      case "x":
        this.setState((prevState) => ({
          multiplying: true,
          adding: false,
          subtracting: false,
          dividing: false,
          memory: prevState.calculatorValue,
          calculatorValue: 0,
        }));
        break;
      case "-":
        this.setState((prevState) => ({
          subtracting: true,
          adding: false,
          multiplying: false,
          dividing: false,
          memory: prevState.calculatorValue,
          calculatorValue: 0,
        }));
        break;
      case "+":
        this.setState((prevState) => ({
          adding: true,
          subtracting: false,
          multiplying: false,
          dividing: false,
          memory: prevState.calculatorValue,
          calculatorValue: 0,
        }));
        break;
      case ".":
        if (this.state.calculatorValue.toString().includes(".")) {
          this.setState((prevState) => ({
            calculatorValue: Number(prevState.calculatorValue),
          }));
        } else {
          this.setState((prevState) => ({
            calculatorValue: prevState.calculatorValue + ".",
            decimal: true,
          }));
        }
        break;
      case "=":
        if (this.state.adding) {
          this.setState((prevState) => ({
            calculatorValue: parseFloat(
              (
                parseFloat(prevState.memory) +
                parseFloat(prevState.calculatorValue)
              ).toFixed(12),
            ),
            adding: false,
            memory: 0,
          }));
        } else if (this.state.subtracting) {
          this.setState((prevState) => ({
            calculatorValue: parseFloat(
              (
                parseFloat(prevState.memory) -
                parseFloat(prevState.calculatorValue)
              ).toFixed(12),
            ),
            subtracting: false,
            memory: 0,
          }));
        } else if (this.state.multiplying) {
          this.setState((prevState) => ({
            calculatorValue: parseFloat(
              (
                parseFloat(prevState.memory) *
                parseFloat(prevState.calculatorValue)
              ).toFixed(12),
            ),
            multiplying: false,
            memory: 0,
          }));
        } else if (this.state.dividing) {
          this.setState((prevState) => ({
            calculatorValue: parseFloat(
              (
                parseFloat(prevState.memory) /
                parseFloat(prevState.calculatorValue)
              ).toFixed(12),
            ),
            dividing: false,
            memory: 0,
          }));
        }
        break;
      case "M+":
        this.setState((prevState) => ({
          memoryValue: prevState.memoryValue + prevState.calculatorValue,
        }));
        break;
      case "MR":
        this.setState((prevState) => ({
          calculatorValue: prevState.memoryValue,
        }));
        break;
      case "MC":
        this.setState({ memoryValue: 0 });
        break;
      default:
        console.log(`Unhandled Button: ${buttonSelected}`);
        break;
    }
  };

  getCalcDisplay = () => {
    const {
      adding,
      multiplying,
      dividing,
      subtracting,
      memory,
      calculatorValue,
    } = this.state;

    let calcAction = "";
    if (adding) calcAction = "+";
    if (multiplying) calcAction = "X";
    if (dividing) calcAction = "/";
    if (subtracting) calcAction = "-";

    if (adding || multiplying || dividing || subtracting) {
      return memory.toString() + calcAction + calculatorValue.toString();
    }
    return calculatorValue.toString();
  };

  renderHitArea = (key, action, param) => {
    const rect = KEY_RECTS[key];
    return (
      <button
        key={key}
        type="button"
        className="cdmqb-hit"
        style={{
          left: `${rect.left}%`,
          top: `${rect.top}%`,
          width: `${rect.width}%`,
          height: `${rect.height}%`,
        }}
        onClick={() => this.handleCalcClick(action, param)}
        aria-label={key}
      />
    );
  };

  render() {
    const { isMobile, onClose } = this.props;
    const { position, skinMarkup, memoryValue } = this.state;

    return (
      <div
        className={`cdmqb-calc-window${isMobile ? " cdmqb-calc-window--mobile" : ""}`}
        style={
          isMobile
            ? undefined
            : { left: `${position.x}px`, top: `${position.y}px` }
        }
      >
        {!isMobile && (
          <div
            className="cdmqb-calc-titlebar"
            onMouseDown={this.handleDragStart}
          >
            <span className="cdmqb-calc-title">Calculator</span>
            {onClose && (
              <button
                type="button"
                className="cdmqb-calc-close"
                onClick={onClose}
                // Keep the close button from starting a drag.
                onMouseDown={(e) => e.stopPropagation()}
                aria-label="Close calculator"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className="cdmqb-calc-container">
          <div className="cdmqb-calc-skin">
            <div
              className="cdmqb-calc-skin-art"
              aria-hidden="true"
              // Safe: skinMarkup only ever comes from our own static asset
              // fetched above, never from user input.
              dangerouslySetInnerHTML={
                skinMarkup ? { __html: skinMarkup } : undefined
              }
            />

            <div
              className="cdmqb-calc-screen"
              style={{
                left: `${DISPLAY_RECT.left}%`,
                top: `${DISPLAY_RECT.top}%`,
                width: `${DISPLAY_RECT.width}%`,
                height: `${DISPLAY_RECT.height}%`,
              }}
            >
              <p className="cdmqb-calc-screen-text">{this.getCalcDisplay()}</p>
              {memoryValue !== 0 && <p className="cdmqb-calc-memory-flag">M</p>}
            </div>

            {this.renderHitArea("negate", "+/-")}
            {this.renderHitArea("sqrt", "sqrt(x)")}
            {this.renderHitArea("inv", "1/x")}
            {this.renderHitArea("divide", "÷")}

            {this.renderHitArea("mrc", "MR")}
            {this.renderHitArea("mminus", "MC")}
            {this.renderHitArea("mplus", "M+")}
            {this.renderHitArea("multiply", "x")}

            {this.renderHitArea("7", "7", "gray")}
            {this.renderHitArea("8", "8", "gray")}
            {this.renderHitArea("9", "9", "gray")}
            {this.renderHitArea("subtract", "-")}

            {this.renderHitArea("4", "4", "gray")}
            {this.renderHitArea("5", "5", "gray")}
            {this.renderHitArea("6", "6", "gray")}
            {this.renderHitArea("add", "+")}

            {this.renderHitArea("1", "1", "gray")}
            {this.renderHitArea("2", "2", "gray")}
            {this.renderHitArea("3", "3", "gray")}
            {this.renderHitArea("equals", "=")}

            {this.renderHitArea("clear", "C")}
            {this.renderHitArea("0", "0", "gray")}
            {this.renderHitArea("decimal", ".")}
          </div>
        </div>
      </div>
    );
  }
}

// ===========================================================================
// SimulationCalculatorCDMQB — hosts the calculator in the CDMQB simulation
// skin (this is the component registered in SimulationSkins.js).
//
// Desktop: the calculator is a draggable floating window whose starting
// position (x: -500) is relative to a right-edge anchor, same as on the
// test page. The anchor here is position: fixed, so the calculator stays in
// view while the case text scrolls underneath it.
//
// Mobile: the calculator drops its own title bar (and close button), so
// this wrapper provides a popup with its own header and close button.
// ===========================================================================

export default class SimulationCalculatorCDMQB extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobile: getIsMobile(),
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    if (this.props.displayCalculator) this.addEscapeListener();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.displayCalculator !== this.props.displayCalculator) {
      if (this.props.displayCalculator) {
        this.addEscapeListener();
      } else {
        this.removeEscapeListener();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.removeEscapeListener();
  }

  handleResize = () => {
    const isMobile = getIsMobile();
    if (isMobile !== this.state.isMobile) this.setState({ isMobile });
  };

  // Escape closes the calculator.
  handleKeyDown = (event) => {
    if (event.key === "Escape") this.props.closeCalculator();
  };

  addEscapeListener = () => {
    document.addEventListener("keydown", this.handleKeyDown);
  };

  removeEscapeListener = () => {
    document.removeEventListener("keydown", this.handleKeyDown);
  };

  render() {
    const { displayCalculator, closeCalculator } = this.props;

    if (!displayCalculator) return null;

    if (this.state.isMobile) {
      return (
        <div
          className="simulationCalculatorOverlayCDMQB"
          onClick={closeCalculator}
        >
          <div
            className="simulationCalculatorPopupCDMQB"
            role="dialog"
            aria-label="Calculator"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="simulationCalculatorPopupHeaderCDMQB">
              <span className="simulationCalculatorPopupTitleCDMQB">
                Calculator
              </span>
              <button
                type="button"
                className="simulationCalculatorPopupCloseCDMQB"
                onClick={closeCalculator}
                aria-label="Close calculator"
              >
                ×
              </button>
            </div>
            <CdmqbCalculator isMobile onClose={closeCalculator} />
          </div>
        </div>
      );
    }

    return (
      <div className="simulationCalculatorAnchorCDMQB">
        <CdmqbCalculator isMobile={false} onClose={closeCalculator} />
      </div>
    );
  }
}
